import axios from "axios";
import { createContext, useContext, useMemo, useReducer, useEffect, useRef, useState } from "react";
import * as global from '../data/global';
import { clearAuthState, saveAuthState, GRAINWAVE_BROWSER_SESSION_KEY, resetAuth401Count, registerAuth401Failure } from '../data/global';
import { createApiEndpoint } from '../index.js';

const REFRESH_RETRY_FLAG = '__gwRefreshRetry';
const REFRESH_URL_RE = /\/(refresh|renew)(?:\?.*)?$/i;
const isRefreshRequest = (cfg) => !!(cfg && typeof cfg.url === 'string' && REFRESH_URL_RE.test(cfg.url));

// Create the authentication context
const AuthContext = createContext();

// Define the possible actions for the authReducer
const ACTIONS = {
    setToken: "setToken",
    clearToken: "clearToken",
};

// Reducer function to handle authentication state changes
const authReducer = (state, action) => {
    switch (action.type) {
        case ACTIONS.setToken:
            // Set the authentication token in axios headers and local storage
            axios.defaults.headers.common["Authorization"] = "Bearer " + action.payload;
            global.gw.token = action.payload;
            global.gw.isAuthenticated = true;
            resetAuth401Count();
            try {
                sessionStorage.setItem(GRAINWAVE_BROWSER_SESSION_KEY, 'true');
            } catch {
                /* private mode */
            }
            // Save authentication state to localStorage
            saveAuthState();

            // Update the state with the new token
            return { ...state, token: action.payload };

        case ACTIONS.clearToken:
            // Clear the authentication token from axios headers and local storage
            delete axios.defaults.headers.common["Authorization"];
            
            // Clear authentication state from localStorage
            clearAuthState();

            global.gw.token = null;
            global.gw.auth = null;
            global.gw.isAuthenticated = false;
            //global.gw.email = null;
            global.gw.siteInfos = [];
            global.gw.refreshToken = null;
            global.gw.profileUser = "";
            global.gw.profilteRole = "";
            global.gw.profileTime = "";
            global.gw.profileDate = "";
            global.gw.bolts = null;
            global.gw.currentWeatherReadings = [];

            resetAuth401Count();
            // Clear saved CustomerSites state (expanded sites and scroll position)
            sessionStorage.removeItem('grainwave_customersites_expanded');
            sessionStorage.removeItem('grainwave_customersites_scroll');
            localStorage.removeItem('grainwave_customersites_expanded');
            localStorage.removeItem('grainwave_customersites_scroll');

            // Update the state by removing the token
            return { ...state, token: null };

        // Handle other actions (if any)

        default:
            console.error(
                `You passed an action.type: ${action.type} which doesn't exist`
            );
    }
};

// Initial state for the authentication context
// Note: global.gw.token is already restored from localStorage in global.js initialization
// Also check localStorage directly as a fallback in case global.gw hasn't been initialized yet
const getInitialToken = () => {
    // global.js restores token + syncs sessionStorage when localStorage has grainwave_token (incl. new tabs).
    if (global.gw.token) {
        return global.gw.token;
    }
    // Fallback: check localStorage directly
    try {
        const token = localStorage.getItem('grainwave_token');
        const auth = localStorage.getItem('grainwave_auth');
        if (token) {
            // Restore to global.gw if not already set (refresh token optional)
            global.gw.token = token;
            global.gw.auth = auth || null;
            global.gw.email = localStorage.getItem('grainwave_email') || null;
            global.gw.isAuthenticated = false;
            
            // Restore user profile if available
            const userProfileStr = localStorage.getItem('grainwave_userProfile');
            if (userProfileStr) {
                try {
                    global.gw.userProfile = JSON.parse(userProfileStr);
                } catch (e) {
                    console.warn('Error parsing userProfile from localStorage:', e);
                }
            }
            
            // Restore bolts if available (needed for encrypted/compressed API calls)
            const bolts = localStorage.getItem('grainwave_bolts');
            if (bolts) {
                global.gw.bolts = bolts;
            }
            const rolesStr = localStorage.getItem('grainwave_roles');
            if (rolesStr) {
                try {
                    global.gw.roles = JSON.parse(rolesStr);
                } catch (e) {
                    console.warn('Error parsing roles from localStorage:', e);
                }
            }
            const profileUserStored = localStorage.getItem('grainwave_profileUser');
            if (profileUserStored) {
                global.gw.profileUser = profileUserStored;
            }
            
            // Set axios header
            axios.defaults.headers.common["Authorization"] = "Bearer " + token;
            return token;
        }
    } catch (error) {
        console.error('Error reading from localStorage:', error);
    }
    return null;
};

const initialData = {
    token: getInitialToken(),
};

// AuthProvider component to provide the authentication context to children
const AuthProvider = ({ children }) => {
    // Use reducer to manage the authentication state
    const [state, dispatch] = useReducer(authReducer, initialData);

    /** Single in-flight refresh promise so concurrent 401s share one /refresh call. */
    const refreshInFlightRef = useRef(null);

    // Set up global axios interceptor to handle 401 errors on all API calls
    useEffect(() => {
        /** Force logout + redirect (used when refresh isn't possible or fails). */
        const hardLogout = (error) => {
            dispatch({ type: ACTIONS.clearToken });
            delete axios.defaults.headers.common["Authorization"];
            global.gw.token = null;
            global.gw.auth = null;
            global.gw.isAuthenticated = false;
            try {
                if (error) error.__gw401SessionLoggedOut = true;
            } catch {
                /* ignore */
            }
            if (window.location.pathname !== '/emailLogin') {
                setTimeout(() => {
                    window.location.href = "/emailLogin";
                }, 100);
            }
        };

        /**
         * POST /refresh with current access+refresh tokens, swap in the new access token, and persist.
         * Returns the new access token string on success, null on failure. Coalesces concurrent callers.
         */
        const tryRefreshAccessToken = () => {
            if (refreshInFlightRef.current) return refreshInFlightRef.current;

            const currentToken = global.gw?.token;
            const currentAuth = global.gw?.auth;
            if (!currentToken || !currentAuth) {
                return Promise.resolve(null);
            }

            const promise = (async () => {
                try {
                    const result = await createApiEndpoint().fetchRefresh(currentToken, currentAuth);
                    if (!result) return null;
                    const splits = String(result).split('|');
                    const newToken = splits[0];
                    if (!newToken) return null;
                    const newAuth = splits.length === 2 && splits[1] ? splits[1] : currentAuth;
                    global.gw.token = newToken;
                    global.gw.auth = newAuth;
                    global.gw.isAuthenticated = true;
                    axios.defaults.headers.common["Authorization"] = "Bearer " + newToken;
                    resetAuth401Count();
                    saveAuthState();
                    dispatch({ type: ACTIONS.setToken, payload: newToken });
                    return newToken;
                } catch {
                    return null;
                } finally {
                    refreshInFlightRef.current = null;
                }
            })();

            refreshInFlightRef.current = promise;
            return promise;
        };

        // Response interceptor: on 401, transparently refresh + retry the original request.
        // Falls back to existing 3-strikes / hard-logout behavior only when refresh isn't possible.
        const responseInterceptor = axios.interceptors.response.use(
            (response) => {
                resetAuth401Count();
                return response;
            },
            async (error) => {
                const status = error?.response?.status;
                const original = error?.config;

                if (status !== 401) {
                    return Promise.reject(error);
                }

                if (global.readImpersonator()) {
                    const restored = global.endImpersonation();
                    if (restored) {
                        window.location.assign('/sites');
                        return Promise.reject(error);
                    }
                }

                // 401 from /refresh|/renew themselves — refresh path is dead, log out immediately.
                if (isRefreshRequest(original)) {
                    hardLogout(error);
                    return Promise.reject(error);
                }

                // Try transparent refresh + single retry when we have both tokens.
                const canRefresh = !!(global.gw?.token && global.gw?.auth);
                if (original && !original[REFRESH_RETRY_FLAG] && canRefresh) {
                    original[REFRESH_RETRY_FLAG] = true;
                    const newToken = await tryRefreshAccessToken();
                    if (newToken) {
                        try {
                            original.headers = original.headers || {};
                            original.headers["Authorization"] = "Bearer " + newToken;
                            // Resolves the original axios call with the retry response — caller never sees the 401.
                            return await axios(original);
                        } catch (retryErr) {
                            // If retry also failed with 401, fall through to logout. Other errors bubble.
                            if (retryErr?.response?.status === 401) {
                                hardLogout(retryErr);
                            }
                            return Promise.reject(retryErr);
                        }
                    }
                    // Refresh attempt failed (e.g. expired refresh token) — log out now.
                    hardLogout(error);
                    return Promise.reject(error);
                }

                // No refresh possible (no auth/token) or already retried: legacy strike-counted logout.
                if (registerAuth401Failure()) {
                    hardLogout(error);
                } else {
                    try {
                        error.__gw401HandledByInterceptor = true;
                    } catch {
                        /* ignore */
                    }
                }
                return Promise.reject(error);
            }
        );

        // Cleanup: remove interceptor when component unmounts
        return () => {
            axios.interceptors.response.eject(responseInterceptor);
        };
    }, []); // Only run once on mount

    // True once validateToken finishes (or immediately when no stored token).
    const [authReady, setAuthReady] = useState(() => !getInitialToken());

    // Validate token on mount if it exists (restored from localStorage)
    useEffect(() => {
        const validateToken = async () => {
            if (!global.gw.token) {
                global.gw.authReady = true;
                setAuthReady(true);
                return;
            }

            try {
                const response = await createApiEndpoint().fetchProfile("direct");

                if (response && response.data) {
                    axios.defaults.headers.common["Authorization"] = "Bearer " + global.gw.token;
                    global.gw.isAuthenticated = true;
                    dispatch({ type: ACTIONS.setToken, payload: global.gw.token });
                } else {
                    console.warn('Token validation: no profile data, clearing stored token');
                    dispatch({ type: ACTIONS.clearToken });
                }
            } catch (error) {
                if (error.response?.status === 401 || (error.message && error.message.endsWith("401"))) {
                    dispatch({ type: ACTIONS.clearToken });
                } else {
                    console.warn('Token validation error (non-auth):', error.message);
                    global.gw.isAuthenticated = false;
                }
            } finally {
                global.gw.authReady = true;
                setAuthReady(true);
            }
        };

        validateToken();
    }, []); // Only run once on mount

    // Function to set the authentication token
    const setToken = (newToken) => {
        // Dispatch the setToken action to update the state
        dispatch({ type: ACTIONS.setToken, payload: newToken });
    };

    // Function to clear the authentication token
    const clearToken = () => {
        // Dispatch the clearToken action to update the state
        dispatch({ type: ACTIONS.clearToken });
    };

    // Memoized value of the authentication context
    const contextValue = useMemo(
        () => ({
            ...state,
            authReady,
            setToken,
            clearToken,
        }),
        [state, authReady]
    );

    // Provide the authentication context to the children components
    return (
        <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
    );
};

// Custom hook to easily access the authentication context
export const useAuth = () => {
    return useContext(AuthContext);
};

export default AuthProvider;